import { Grid2 } from '@mui/material';
import {
  useStripe,
  useElements,
  CardNumberElement,
} from '@stripe/react-stripe-js';
import { navigate } from 'gatsby';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import RoundedButton from '@components/RoundedButton/index';
import TextInputBox from '@components/TextInputBox';

import { isDevelopment } from '@constants/env';
import { MANAGE_ACCOUNT } from '@constants/routes';

import { useUpdateStripeCustomer } from '@hooks/api/stripe';

import type { ManageAccountModel } from '@models/account';

import CheckoutForm from '@views/Onboarding/PaymentInfo/CheckoutForm';

const messages = defineMessages({
  name: {
    id: 'changePaymentForm.name',
    defaultMessage: 'Name on Credit Card',
  },
  cardError: {
    id: 'changePaymentForm.cardError',
    defaultMessage: 'An error occurred while trying to validate your card.',
  },
  title: {
    id: 'changePaymentForm.title',
    defaultMessage: 'Payment Method',
  },
  nameError: {
    id: 'changePaymentForm.nameError',
    defaultMessage: 'Must provide cardholder name',
  },
  submit: {
    id: 'GLOBAL.submit',
    defaultMessage: 'Submit',
  },
});

interface PaymentFormProps {
  account?: ManageAccountModel;
  loading: boolean;
}

const ChangePaymentForm: React.FC<PaymentFormProps> = ({
  account,
  loading: baseLoading,
}) => {
  const { formatMessage } = useIntl();

  const stripe = useStripe();
  const elements = useElements();

  const [name, setName] = useState('');
  const [errors, setErrors] = useState({
    name: '',
    payment: '',
  });

  const [updateStripeCustomer, updateStripeCustomerLoading] =
    useUpdateStripeCustomer();
  const loading = baseLoading || updateStripeCustomerLoading;

  async function submitForm() {
    if (!name) {
      setErrors({ ...errors, name: formatMessage(messages.nameError) });
      return;
    }

    try {
      if (!stripe || !elements || !account?.account_id) {
        return;
      }
      const card = elements.getElement(CardNumberElement);
      if (!card) {
        setErrors({
          name: name ? '' : formatMessage(messages.name),
          payment: formatMessage(messages.cardError),
        });
        return;
      }

      const { token, error } = await stripe.createToken(card, {
        name,
      });

      if (error?.message) {
        setErrors({ ...errors, payment: error.message });
        return;
      } else if (error || !token.card) {
        setErrors({ ...errors, payment: formatMessage(messages.cardError) });
        return;
      }

      await updateStripeCustomer({
        input: {
          name,
          source: token.id,
          account_id: account.account_id,
        },
      });

      navigate(MANAGE_ACCOUNT);
    } catch (e) {
      if (isDevelopment) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    }
  }

  return (
    <ContentLoader loading={loading}>
      <Grid2 container>
        <Grid2 size={{ xs: 12 }}>
          <TextInputBox
            autoComplete="name"
            label={formatMessage(messages.name)}
            helperText={errors.name}
            onChange={(e) => setName(e.target.value)}
            value={name}
          />
        </Grid2>
        <Grid2 size={{ xs: 12 }}>
          <CheckoutForm error={errors.payment} />
        </Grid2>
      </Grid2>

      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <RoundedButton onClick={submitForm}>
        {formatMessage(messages.submit)}
      </RoundedButton>
    </ContentLoader>
  );
};

export default ChangePaymentForm;
